import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { Icon } from "../Icon"
import { ComponentError } from "../util/components"
import useClickOutside from "../util/hooks/useClickOutside"
import {
  ArrowIcon,
  Curtain,
  CurtainOption,
  SearchWrapper,
  SelectMultipleDisplay,
  SelectedOption,
  Wrapper,
} from "./components"
import type {
  SelectMultipleOption,
  SelectMultipleProps,
  SelectRef,
} from "./types"

const BaseSelectMultiple = <ValueType extends string | number, ExtraType = any>(
  {
    className,
    id,
    role,

    selected,
    options,

    error,
    errorTimeout,
    hideErrorMessage = false,

    placeholder = "Select from list...",
    noOptionsText = "No more options",
    searchable = false,
    tabIndex = 0,
    disabled = false,

    inline = false,
    width = "332px",
    fullWidth = false,
    minWidth,
    maxWidth,
    minHeight = "32px",

    color = "text.dark",
    background = "white",

    iconProps,
    clickOutsideExclude = [],

    onErrorTimeout = () => void 0,
    onClick = () => void 0,
    onKeyDown = () => void 0,
    onOpen = () => true,
    onClose = () => true,
    onFilterChange = () => void 0,
    onCurtainScroll,
    onChange,
  }: SelectMultipleProps<ValueType, ExtraType>,
  ref?: React.ForwardedRef<SelectRef>,
) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const curtainRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [filter, setFilter] = useState("")
  const [internalError, setInternalError] = useState(error)

  useImperativeHandle(ref, () => ({
    toggleOpen(open) {
      setOpen((v) => (typeof open === "undefined" ? !v : open))
    },
  }))

  useEffect(() => {
    setInternalError(error)
  }, [error])

  const updateFilter = useCallback(
    (filter: string) => {
      setFilter(filter)
      onFilterChange(filter)
    },
    [onFilterChange],
  )

  useEffect(() => {
    if (!open) {
      setActiveIndex(-1)
      updateFilter("")
    }
  }, [open, updateFilter])

  useEffect(() => {
    if (disabled && open) setOpen(false)
  }, [disabled, open])

  useEffect(() => {
    const el = curtainRef.current
    if (!el) return

    try {
      const activeOption = el.querySelectorAll(".--select-curtain-option")[
        activeIndex
      ]
      if (!activeOption) return
      activeOption.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    } catch {}
  }, [activeIndex])

  const [selectedOptions, unSelectedOptions] = useMemo(
    () =>
      options.reduce(
        ([sel, unSel], o) => {
          if (selected.includes(o.value)) sel.push(o)
          else unSel.push(o)
          return [sel, unSel]
        },
        [[], []] as [
          SelectMultipleOption<ValueType, ExtraType>[],
          SelectMultipleOption<ValueType, ExtraType>[],
        ],
      ),
    [selected, options],
  )
  const filteredOptions = useMemo(
    () =>
      !searchable || filter === ""
        ? unSelectedOptions
        : unSelectedOptions.filter(
            typeof searchable === "boolean"
              ? (option) =>
                  typeof option.label == "string"
                    ? option.label
                        .toLocaleLowerCase()
                        .includes(filter.toLocaleLowerCase())
                    : true
              : searchable === "custom"
                ? () => true
                : (option) => searchable(option, filter),
          ),
    [unSelectedOptions, filter, searchable],
  )

  useClickOutside(
    wrapperRef,
    () => toggleOpen(false),
    true,
    ...clickOutsideExclude,
  )

  function toggleOpen(doOpen: boolean) {
    if (disabled) return
    const proceed = doOpen ? onOpen() : onClose()
    if (proceed !== false) setOpen(doOpen)
  }

  function cycleActive(delta: number) {
    const clamp = (n: number) => Math.max(-1, Math.min(n, options.length))
    setActiveIndex((idx) => clamp(idx + delta))
  }

  function handleAdd(option: SelectMultipleOption<ValueType, ExtraType>) {
    const newSelected = [...selected, option.value]
    onChange(newSelected, option.value, "add", option)
  }

  function handleRemove(option: SelectMultipleOption<ValueType, ExtraType>) {
    const newSelected = selected.filter((v) => v !== option.value)
    onChange(newSelected, option.value, "remove", option)
  }

  function handleKeyDown(evt: React.KeyboardEvent<HTMLDivElement>) {
    onKeyDown(evt)
    if (evt.defaultPrevented) return

    switch (evt.key) {
      case "ArrowDown":
        open ? cycleActive(1) : toggleOpen(true)
        break
      case "ArrowUp":
        open && cycleActive(-1)
        break
      case "Enter":
        if (!open) toggleOpen(true)
        else if (activeIndex > -1 && activeIndex < filteredOptions.length) {
          handleAdd(filteredOptions[activeIndex])
          setActiveIndex((i) => Math.max(i - 1, 0))
        }
        break
    }
  }

  function handleErrorTimeout() {
    setInternalError(null)
    onErrorTimeout()
  }

  return (
    <Wrapper
      className={className}
      id={id}
      role={role}
      ref={wrapperRef}
      disabled={disabled}
      width={fullWidth ? "100%" : width}
      minWidth={minWidth}
      maxWidth={maxWidth}
      inline={inline}
      onKeyDown={handleKeyDown}
      onClick={onClick}
    >
      <ComponentError
        className="--select-multiple-error"
        error={internalError}
        hide={hideErrorMessage}
        timeout={errorTimeout}
        onTimeout={handleErrorTimeout}
      />

      <SelectMultipleDisplay
        className={
          "--select-display" + (disabled ? " --select-display-disabled" : "")
        }
        open={open}
        tabIndex={tabIndex}
        disabled={disabled}
        minHeight={minHeight}
        color={color}
        background={background}
        onClick={() => toggleOpen(!open)}
      >
        <div className="--select-display-inner">
          {selectedOptions.map((option) => (
            <SelectedOption
              className="--select-display-selected"
              key={option.value}
              color={option.color ?? "primary"}
              onClick={(e) => e.stopPropagation()}
            >
              {option.label}
              <Icon
                icon="times"
                type="light"
                color="gray6"
                hoverColor="white"
                onClick={() => handleRemove(option)}
              />
            </SelectedOption>
          ))}
          {!selectedOptions.length && (
            <span className="--select-placeholder">{placeholder}</span>
          )}
        </div>
        <ArrowIcon
          className="--select-arrow-icon"
          icon={open ? "chevron-up" : "chevron-down"}
          type="light"
          color="gray6"
          {...iconProps}
        />
      </SelectMultipleDisplay>

      {open && (
        <Curtain
          ref={curtainRef}
          className="--select-curtain"
          tabIndex={tabIndex}
          color={color}
          background={background}
          onScroll={onCurtainScroll}
        >
          <div className="--select-curtain-inner">
            {!!searchable && (
              <SearchWrapper className="--select-curtain-search">
                <input
                  value={filter}
                  onChange={(e) => updateFilter(e.target.value)}
                />
              </SearchWrapper>
            )}

            {filteredOptions.map((option, index) => {
              const classNames = ["--select-curtain-option"]
              activeIndex === index &&
                classNames.push("--select-curtain-option-active")

              const className = classNames.join(" ")

              return (
                <CurtainOption
                  className={className}
                  key={option.value}
                  active={activeIndex === index}
                  onClick={() => handleAdd(option)}
                >
                  {option.label}
                </CurtainOption>
              )
            })}

            {!filteredOptions.length && (
              <CurtainOption
                className="--select-curtain-option --select-curtain-option-disabled"
                disabled
                active={false}
              >
                {noOptionsText}
              </CurtainOption>
            )}
          </div>
        </Curtain>
      )}
    </Wrapper>
  )
}

export const SelectMultiple = React.forwardRef(BaseSelectMultiple) as <
  ValueType extends string | number,
  ExtraType = any,
>(
  props: SelectMultipleProps<ValueType, ExtraType> & {
    ref?: React.ForwardedRef<SelectRef>
  },
) => ReturnType<typeof BaseSelectMultiple>
